import axiosInstance from "../axiosConfig";
import {
  Assignment,
  AssignmentData,
  AssignmentResponse,
  AssignmentTestDraft,
} from "./assignmentService.types";

import { logger } from "utils/logger";

const appendTestConfig = (
  formData: FormData,
  config: AssignmentTestDraft | null | undefined,
) => {
  if (config === undefined) return;
  if (config === null) {
    formData.append("test_config", "null");
    return;
  }

  const attachmentKeys = new Map<File, string>();
  const payload = {
    questions: config.questions.map((question) => {
      let attachmentKey: string | undefined;
      if (question.attachment_file) {
        attachmentKey = attachmentKeys.get(question.attachment_file);
        if (!attachmentKey) {
          attachmentKey = `test_question_attachment_${attachmentKeys.size}`;
          attachmentKeys.set(question.attachment_file, attachmentKey);
          formData.append(attachmentKey, question.attachment_file);
        }
      }
      const reusedAttachmentId = !question.id && !question.attachment_file && !question.remove_attachment
        ? question.attachment?.id
        : undefined;
      return {
        ...(question.id ? { id: question.id } : {}),
        question_type: question.question_type,
        text: question.text,
        comment: question.comment,
        ...(question.remove_attachment ? { remove_attachment: true } : {}),
        ...(attachmentKey ? { attachment_key: attachmentKey } : {}),
        ...(reusedAttachmentId ? { attachment_id: reusedAttachmentId } : {}),
        options: question.options.map((option) => ({
          ...(option.id ? { id: option.id } : {}),
          text: option.text,
          is_correct: option.is_correct,
        })),
      };
    }),
  };
  formData.append("test_config", JSON.stringify(payload));
};

export const fetchAssignments = async (
  courseId: number,
  lessonId?: number,
  page: number = 1,
  pageSize: number = 10,
  ordering: string = "",
): Promise<AssignmentResponse> => {
  const url = `/courses/lessons/${lessonId}/assignments/`;
  try {
    const response = await axiosInstance.get<AssignmentResponse>(url, {
      params: { page, page_size: pageSize, ordering },
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении списка домашних заданий:", error);
    throw error;
  }
};

export const fetchAssignmentById = async (
  lessonId: number,
  assignmentId: number,
): Promise<Assignment> => {
  const url = `/courses/lessons/${lessonId}/assignments/${assignmentId}/`;
  try {
    const response = await axiosInstance.get<Assignment>(url);
    return response.data;
  } catch (error) {
    logger.error(`Ошибка при получении задания с ID ${assignmentId}:`, error);
    throw error;
  }
};

export const createAssignment = async (
  courseId: number,
  lessonId: number,
  assignmentData: AssignmentData,
): Promise<Assignment> => {
  const url = `/courses/lessons/${lessonId}/assignments/`;

  const formData = new FormData();
  formData.append("title", assignmentData.title);
  formData.append("content", assignmentData.content);
  formData.append("lesson", String(lessonId));
  formData.append("is_required", assignmentData.is_required ? "true" : "false");
  if (assignmentData.video_url) formData.append("video_url", assignmentData.video_url);
  appendTestConfig(formData, assignmentData.test_config);

  if (assignmentData.files) {
    assignmentData.files.forEach((file) => {
      formData.append("files", file);
    });
  }

  try {
    const response = await axiosInstance.post<Assignment>(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании домашнего задания:", error);
    throw error;
  }
};

export const updateAssignment = async (
  courseId: number,
  lessonId: number,
  assignmentId: number,
  assignmentData: AssignmentData,
): Promise<Assignment> => {
  const url = `/courses/lessons/${lessonId}/assignments/${assignmentId}/`;

  const formData = new FormData();
  formData.append("title", assignmentData.title);
  formData.append("content", assignmentData.content);
  formData.append("lesson", String(lessonId));
  formData.append("is_required", assignmentData.is_required ? "true" : "false");
  if (assignmentData.video_url) formData.append("video_url", assignmentData.video_url);
  else formData.append("video_url", "");
  appendTestConfig(formData, assignmentData.test_config);

  if (assignmentData.files_from_storage !== undefined) {
    formData.append("files_from_storage", JSON.stringify(assignmentData.files_from_storage));
  }

  if (assignmentData.file_ids !== undefined) {
    formData.append("file_ids", assignmentData.file_ids || "");
  }

  if (assignmentData.files) {
    assignmentData.files.forEach((file) => {
      formData.append("files", file);
    });
  }

  try {
    const response = await axiosInstance.put<Assignment>(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении домашнего задания:", error);
    throw error;
  }
};

export const deleteAssignment = async (
  courseId: number,
  lessonId: number,
  assignmentId: number,
): Promise<void> => {
  const url = `/courses/lessons/${lessonId}/assignments/${assignmentId}/`;

  try {
    await axiosInstance.delete(url);
  } catch (error) {
    logger.error("Ошибка при удалении домашнего задания:", error);
    throw error;
  }
};
